import { useRef, useState, useEffect } from 'react';
import { useUser } from '@clerk/react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import BasicModal from './BasicModal';
import Image from '../Image';
import { Mixpanel } from '../../services/Mixpanel';
import { updateProfileImage } from '../../services/UserService';

export default function ChangeProfilePictureModal({ showModal, setShowModal }) {
  const { user } = useUser();
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Revoke object URL on unmount or when previewUrl changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset state when modal closes
  useEffect(() => {
    if (!showModal) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setIsUploading(false);
      setSaved(false);
      setErrorMessage('');
    }
  }, [showModal]); // intentionally omit previewUrl to avoid re-running on every preview change

  const uploadFile = async (file) => {
    setIsUploading(true);
    setSaved(false);
    setErrorMessage('');
    try {
      await updateProfileImage(file);
      await user.reload();
      Mixpanel.track('Profile Picture Updated', {
        file_type: file.type,
        file_size: file.size,
      });
      setSaved(true);
      setTimeout(() => setShowModal(false), 300);
    } catch (e) {
      setErrorMessage('There was an error updating your profile picture. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('Image must be 10MB or smaller.');
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));
    uploadFile(file);
  };

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      title='Change Profile Picture'
      maxWidth='sm:max-w-[280px]'>
      <div className='flex flex-col items-center gap-4 mt-4'>
        <Image
          src={previewUrl || user.imageUrl}
          referrerPolicy='no-referrer'
          alt='Profile picture'
          className='mx-auto size-28 rounded-full object-cover'
        />

        <input
          type='file'
          accept='image/*'
          ref={fileInputRef}
          className='hidden'
          onChange={handleFileChange}
        />

        {!isUploading && !saved && (
          <button
            type='button'
            className='w-full px-3 py-2 font-semibold  text-white rounded-md hover:bg-emerald-500 bg-emerald-600'
            onClick={() => fileInputRef.current.click()}>
            <span>Choose photo</span>
          </button>
        )}

        {isUploading && <p className=' text-gray-500 py-2'>Saving...</p>}
        {saved && <p className='font-medium text-emerald-600 py-2'>Saved!</p>}
        {errorMessage && <p className=' text-red-600 py-2'>{errorMessage}</p>}
      </div>
    </BasicModal>
  );
}
